/**
 * DuckDB Context - Provides DuckDB-WASM state throughout the app
 *
 * Manages both:
 * - Local files (user uploads)
 * - Fetched datasets (from remote queries, cached locally)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as duckdbService from '../services/duckdb';
import type { LocalFile, QueryResult, TableSchema, DatasetMetadata } from '../services/duckdb';

interface DuckDBContextValue {
  // State
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  localFiles: LocalFile[];
  fetchedDatasets: DatasetMetadata[];

  // Local file actions
  addFile: (file: File) => Promise<string>;
  removeFile: (tableName: string) => Promise<void>;

  // Query execution
  executeQuery: (sql: string) => Promise<QueryResult>;
  getSchemas: () => Promise<TableSchema[]>;

  // Fetched data actions
  storeFetchedData: (
    tableName: string,
    data: Record<string, any>[],
    metadata: Omit<DatasetMetadata, 'tableName' | 'fetchedAt'>
  ) => Promise<void>;
  removeFetchedDataset: (tableName: string) => Promise<void>;
  clearFetchedDatasets: () => Promise<void>;
}

const DuckDBContext = createContext<DuckDBContextValue | null>(null);

export function DuckDBProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const [fetchedDatasets, setFetchedDatasets] = useState<DatasetMetadata[]>([]);

  // Initialize DuckDB on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        await duckdbService.initDuckDB();
        if (mounted) {
          setIsInitialized(true);
          setLocalFiles(duckdbService.getRegisteredFiles());
          setFetchedDatasets(duckdbService.getFetchedDatasets());
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize DuckDB');
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // Add a file
  const addFile = useCallback(async (file: File): Promise<string> => {
    setIsLoading(true);
    setError(null);

    try {
      const tableName = await duckdbService.registerFile(file);
      setLocalFiles(duckdbService.getRegisteredFiles());
      return tableName;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add file';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Remove a file
  const removeFile = useCallback(async (tableName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await duckdbService.unregisterFile(tableName);
      setLocalFiles(duckdbService.getRegisteredFiles());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove file';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Execute a query
  const executeQuery = useCallback(async (sql: string): Promise<QueryResult> => {
    if (!isInitialized) {
      return {
        success: false,
        error: 'DuckDB not initialized',
        columns: [],
        rows: [],
        rowCount: 0
      };
    }

    return duckdbService.executeQuery(sql);
  }, [isInitialized]);

  // Get all schemas
  const getSchemas = useCallback(async (): Promise<TableSchema[]> => {
    if (!isInitialized) return [];
    return duckdbService.getAllTableSchemas();
  }, [isInitialized]);

  // Store fetched data from remote query
  const storeFetchedData = useCallback(async (
    tableName: string,
    data: Record<string, unknown>[],
    metadata: Omit<DatasetMetadata, 'tableName' | 'fetchedAt'>
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await duckdbService.storeFetchedData(tableName, data, metadata);
      setFetchedDatasets(duckdbService.getFetchedDatasets());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to store fetched data';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Remove a fetched dataset
  const removeFetchedDataset = useCallback(async (tableName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await duckdbService.removeFetchedDataset(tableName);
      setFetchedDatasets(duckdbService.getFetchedDatasets());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove dataset';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Clear all fetched datasets
  const clearFetchedDatasets = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await duckdbService.clearFetchedDatasets();
      setFetchedDatasets([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clear datasets';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <DuckDBContext.Provider value={{
      isInitialized,
      isLoading,
      error,
      localFiles,
      fetchedDatasets,
      addFile,
      removeFile,
      executeQuery,
      getSchemas,
      storeFetchedData,
      removeFetchedDataset,
      clearFetchedDatasets
    }}>
      {children}
    </DuckDBContext.Provider>
  );
}

export function useDuckDBContext(): DuckDBContextValue {
  const context = useContext(DuckDBContext);
  if (!context) {
    throw new Error('useDuckDBContext must be used within a DuckDBProvider');
  }
  return context;
}

export default DuckDBContext;
